import FormRow from 'Components/FormRow';
import { Container, Item } from 'Components/Grid';
import InputHandler from 'Components/InputHandler';
import { gte } from 'ramda';
import * as React from 'react';
import { formatStrenghtString } from 'Routes/Medication/utils';
import { isNil, path } from 'Utility/ramdaReplacement';
import { valueFilter } from '../utils';
import DosingInstructions from './DosingInstructions';

// Format strengths to string and return string based on array item index
const strengthOptionFormatter =
  (strArr: IStrengths[]) =>
  (index: number): string =>
    strArr[index] ? formatStrenghtString(strArr[index]) : '';

const changeValue =
  (formData: IOwnProps['formData'], editIndex: number, name: string) =>
  (values: TOnChangeValues): void => {
    let value = values[name];
    if (!isNil(value)) {
      const regimen = formData.document.regimen ? [...formData.document.regimen] : [];
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (regimen) {
        if (name === 'strengths') {
          const newStrengths = JSON.parse(JSON.stringify((regimen[editIndex] as any)?.strengths ?? []));
          const strengthFormatted = { [formData.document.medicationSubstances ?? 's']: value.toString() };
          if (newStrengths.length > 0) {
            newStrengths[0] = strengthFormatted;
          } else {
            newStrengths.push(strengthFormatted);
          }
          value = newStrengths;
        }
        regimen[editIndex] = { ...regimen[editIndex], [name]: value } as IRegimenDefault;
        if (formData.document.isClinicalStudy && Array.isArray(regimen[editIndex].dosages)) {
          regimen[editIndex] = { ...regimen[editIndex], dosages: [] } as IRegimenDefault;
        }
        formData.onChange?.({ regimen });
      }
    }
  };

export const StrengthSelect = ({
  formData,
  availableStrengths,
  strengthsOnChange,
  strengths,
  editIndex,
}: IOwnProps): React.JSX.Element => {
  return (
    <FormRow title="medication.strength" headerWidth={3}>
      <Container alignItems="center">
        <Item>
          {path(['isClinicalStudy', 0], formData.document) === true ? (
            <InputHandler
              type="TextField"
              name="strengths"
              formData={{
                onChange: changeValue(formData, editIndex ?? 0, 'strengths'),
                document: {
                  strengths:
                    path(
                      ['regimen', editIndex ?? 0, 'strengths', 0, formData.document.medicationSubstances ?? 's'],
                      formData.document,
                    ) ?? '',
                },
              }}
              editing={true}
              fullWidth={true}
              placeholder="medication.strength"
            />
          ) : (
            <InputHandler
              type="Select"
              name="strengths"
              editing={true}
              options={availableStrengths.map((_, i) => i)} // Get indexes from strengths
              optionFormatter={strengthOptionFormatter(availableStrengths) as (opt: string | number) => string}
              formData={{
                onChange: strengthsOnChange,
                document: { strengths: valueFilter(availableStrengths, strengths, gte) }, // Fetch indexes of strength in form data, from all strengths
              }}
              placeholder="medication.strength"
              autoSelectSingle
            />
          )}
        </Item>
        <DosingInstructions medication={formData.document} />
      </Container>
    </FormRow>
  );
};

interface IOwnProps {
  formData: { onChange?: any; document: IMedication };
  availableStrengths: { [key: string]: string }[];
  strengthsOnChange: (e: TOnChangeValues) => void;
  strengths: IStrengths[];
  editIndex?: number;
}
